/**
 * Fiches plateau — séances types conformes au livret REMC catégorie A
 *
 * Chaque fiche suit la trame "Conduite" du canevas pédagogique
 * (4 phases · 9 étapes) et référence une sous-compétence officielle
 * du livret d'apprentissage moto (C1-a à C4-g).
 *
 * Format identique à PedagogicalCanvas pour rendu direct.
 */

export const FICHES_PLATEAU = [
  {
    slug: 'demarrer-sarreter',
    competence: 'C1-b',
    competenceCat: 'C1',
    competenceLabel: 'Maîtriser le maniement de la moto',
    title: "Démarrer et s'arrêter en toutes circonstances",
    kicker: "C1-b · Plateau · 45 min · Individuel",
    accent: '#1d4ed8',
    icon: 'Play',
    duree: '45 min',
    summary:
      "Maîtrise du démarrage et de l'arrêt : départ en montée, arrêt précis à un point, gestion du point de patinage.",
    meta: {
      duree: '45 minutes',
      niveau: 'C1-b · Maniement (REMC)',
      lieu: 'Plateau · zone départ 30 m + montée 5 %',
      materiel: 'Plot de départ · plot d\'arrêt · cône intermédiaire',
    },
    steps: {
      evalStatique: {
        content: "Avant de monter en moto :",
        items: [
          "« Comment te sens-tu sur le démarrage depuis la dernière séance ? »",
          "« Tu repères le point de patinage facilement ? »",
          "Vérifier C1-a (connaître les commandes) déjà validé dans le livret.",
        ],
      },
      evalDynamique: {
        content: "3 départs/arrêts à plat sans consigne, observation :",
        items: [
          "Trouve-t-il le point de patinage du premier coup ?",
          "L'embrayage est-il dosé ou claqué ?",
          "L'arrêt est-il précis au plot ou approximatif (± 50 cm) ?",
        ],
      },
      objectif: {
        content:
          "« À la fin de la séance, tu sauras <strong>démarrer et t'arrêter avec précision</strong> sur le plat ET en montée, en restant à distance ≤ 30 cm du plot d'arrêt. »",
        notes: "Référence officielle livret REMC : C1-b — Démarrer et s'arrêter en toutes circonstances.",
      },
      criteres: {
        content: "Tu réussis si :",
        items: [
          "Démarrage en moins de 3 secondes sans à-coup",
          "Pas de blocage moteur (calage)",
          "Arrêt précis à ≤ 30 cm du plot",
          "Pied gauche au sol stabilisé à l'arrêt",
        ],
      },
      cours: {
        content: "5 min de théorie au tableau / debout devant la moto :",
        items: [
          "Schéma : friction embrayage = transmission progressive (point de patinage)",
          "3 phases : embrayage → gaz → démarrage",
          "Arrêt = inverse : gaz coupé → frein avant → débrayage juste avant l'arrêt",
        ],
      },
      demonstration: {
        content: "Démo en 3 temps :",
        items: [
          "1) Démo MONITEUR commentée : départ + arrêt 1 passage (10 m)",
          "2) Démo MONITEUR commentée guidée : élève suit en marchant à côté",
          "3) Tu fais 1 passage avec verbalisation à voix haute (« j'embraye, je donne du gaz, je pars »)",
        ],
      },
      guidage: {
        content: "Pratique encadrée — diminution progressive du guidage :",
        items: [
          "<strong>FORT</strong> : moniteur compte les temps à la radio « 1-embraye, 2-gaz, 3-pars »",
          "<strong>MOYEN</strong> : juste « gaz » au bon moment",
          "<strong>FAIBLE</strong> : moniteur silencieux, fait un signe pour le départ",
        ],
      },
      autonomie: {
        content: "5 passages CONSÉCUTIFS sans aucune intervention :",
        items: [
          "Si 5/5 OK → objectif validé, on passe au départ en MONTÉE",
          "Si < 5/5 → on retravaille le geste (méthode du « grain de sable »)",
        ],
      },
      bilan: {
        content: "Tu parles EN PREMIER :",
        items: [
          "« Sur une échelle de 1 à 10, à combien tu te sens à l'aise sur le démarrage ? »",
          "« Qu'est-ce qui était plus facile : à plat ou en montée ? »",
          "« Qu'est-ce que tu vas pratiquer chez toi mentalement avant la prochaine séance ? »",
          "Moniteur : reformule 2 réussites + 1 axe (souvent : être plus progressif sur le gaz) + objectif prochain (C1-c).",
        ],
        notes: "Inscrire C1-b validé dans le livret REMC avec date + signature.",
      },
    },
  },
  {
    slug: 'tourner-ralenti-moteur',
    competence: 'C2-d',
    competenceCat: 'C2',
    competenceLabel: 'Appréhender la route et circuler',
    title: "Tourner à gauche au ralenti moteur",
    kicker: 'C2-d · Plateau · 45 min · Individuel',
    accent: '#7c3aed',
    icon: 'CornerDownLeft',
    duree: '45 min',
    summary:
      "Virage serré à gauche au pas (≤ 10 km/h) avec embrayage glissant, regard à la sortie, pied droit sur le frein arrière.",
    meta: {
      duree: '45 minutes',
      niveau: 'C2-d · Appréhender la route (REMC)',
      lieu: 'Plateau · parcours en U avec cônes',
      materiel: '6 cônes en U serré · 1 ligne d\'arrivée tracée',
    },
    steps: {
      evalStatique: {
        content: "Avant pratique :",
        items: [
          "« Tu te souviens du point de patinage de la dernière séance ? »",
          "« Comment tu places ton regard quand tu tournes ? »",
          "Vérifier C1-b validé dans le livret.",
        ],
      },
      evalDynamique: {
        content: "2 passages dans le U sans consigne — observer :",
        items: [
          "Le regard est-il porté à la sortie du virage ou sur l'avant ?",
          "L'embrayage est-il maintenu en patinage ou relâché ?",
          "Le pied droit est-il sur le frein arrière ou rien ?",
        ],
      },
      objectif: {
        content:
          "« À la fin, tu sauras <strong>tourner à gauche au ralenti moteur</strong>, embrayage glissant et pied droit sur le frein, sans poser le pied au sol. »",
        notes: "Référence livret REMC : C2-d. Pré-requis du demi-tour serré et de la circulation lente.",
      },
      criteres: {
        content: "Tu réussis si :",
        items: [
          "Aucune pose du pied gauche pendant le virage",
          "Regard porté à la sortie (visible par tourner-tête)",
          "Embrayage GLISSANT (entendre le ralenti moteur stable)",
          "Pied droit en appui doux sur le frein arrière",
          "Trajectoire propre sans coup de guidon",
        ],
      },
      cours: {
        content: "10 min de théorie :",
        items: [
          "Schéma vu de dessus : trajectoire en arc + position du regard au point B (sortie)",
          "Le secret du virage lent : <strong>embrayage glissant + filet de gaz + frein arrière</strong>",
          "Pourquoi le pied droit sur le frein ? Pour ne pas calculer un frein avant + équilibre",
        ],
      },
      demonstration: {
        content: "Démo en 3 temps :",
        items: [
          "1) Démo commentée au ralenti moteur — moniteur verbalise chaque action",
          "2) Démo guidée — élève suit en marchant à pied à côté de la moto",
          "3) Tu fais 1 passage à très basse vitesse pour ressentir la position",
        ],
      },
      guidage: {
        content: "Pratique progressive :",
        items: [
          "<strong>FORT</strong> : « embraye-débraye doucement, regarde où tu vas »",
          "<strong>MOYEN</strong> : juste « regard sortie » au bon moment",
          "<strong>FAIBLE</strong> : silencieux, l'élève s'auto-corrige",
        ],
      },
      autonomie: {
        content: "5 passages consécutifs sans pose de pied :",
        items: [
          "Critère absolu : 0 pose de pied sur 5 passages = validé",
          "Mesurer le temps moyen (un U doit prendre 6-8 sec, pas 3 sec)",
        ],
      },
      bilan: {
        content: "Tu parles d'abord :",
        items: [
          "« À quel moment tu te sens le moins stable ? »",
          "« Tu sens la différence avec et sans frein arrière ? »",
          "« Tu veux qu'on monte la difficulté avec un U plus serré ? »",
          "Moniteur : 2 réussites + 1 axe (souvent : tourner-tête trop tardif) + objectif prochain : demi-tour serré (C2-e).",
        ],
        notes: "Phrase-clé livret : « Au ralenti, le frein arrière est ton 3e point d'appui. »",
      },
    },
  },
  {
    slug: 'demi-tour-serre',
    competence: 'C2-e',
    competenceCat: 'C2',
    competenceLabel: 'Appréhender la route et circuler',
    title: 'Réaliser un demi-tour serré',
    kicker: 'C2-e · Plateau · 45 min · Individuel',
    accent: '#9333ea',
    icon: 'RotateCw',
    duree: '45 min',
    summary:
      "Demi-tour à 180° dans un espace réduit (largeur < 4 m) sans poser le pied, base de l'ETM moto.",
    meta: {
      duree: '45 minutes',
      niveau: 'C2-e · Appréhender la route (REMC)',
      lieu: 'Plateau · zone 4 m × 8 m balisée',
      materiel: '4 cônes pour balisage · 2 piquets d\'entrée/sortie',
    },
    steps: {
      evalStatique: {
        content: "Avant pratique :",
        items: [
          "« Tu te sens prêt après le travail de C2-d (tourner au ralenti) ? »",
          "« Quel pied tu poses naturellement quand tu hésites ? »",
          "Vérifier C2-d (tourner ralenti) validé.",
        ],
      },
      evalDynamique: {
        content: "1 demi-tour libre — observer :",
        items: [
          "Pose le pied combien de fois ? À quel moment du virage ?",
          "Regard porté loin ou bloqué sur l'avant ?",
          "Largeur du virage utilisée (trop large = manque de confiance) ?",
        ],
      },
      objectif: {
        content:
          "« À la fin, tu sauras réaliser un <strong>demi-tour à 180° dans une zone de 4 m de large</strong>, sans poser le pied. »",
        notes: "Référence livret REMC : C2-e. Compétence évaluée sur le plateau de l'examen pratique.",
      },
      criteres: {
        content: "Tu réussis si :",
        items: [
          "Aucune pose de pied",
          "Largeur utilisée < 4 m (mesurer)",
          "Regard à la sortie visible pendant le virage",
          "Vitesse stable au ralenti moteur (pas d'à-coup)",
          "Trajectoire ronde, sans rupture d'angle",
        ],
      },
      cours: {
        content: "10 min :",
        items: [
          "Schéma vu de dessus : trajectoire en goutte d'eau (entrée droite, sortie sortie franche)",
          "Recette : embrayage glissant + filet de gaz + frein arrière + REGARD",
          "Le regard est l'élément différentiateur entre le 4 m et le 6 m",
        ],
      },
      demonstration: {
        content: "Démo en 3 temps :",
        items: [
          "1) Démo lente commentée — moniteur fait le demi-tour en 8-10 sec",
          "2) Démo guidée — élève marche dans la trajectoire à pied pour la mémoriser",
          "3) Tu fais un demi-tour à basse vitesse pour sentir la largeur",
        ],
      },
      guidage: {
        content: "Pratique :",
        items: [
          "<strong>FORT</strong> : « tourner-tête maintenant ! regarde le piquet de sortie »",
          "<strong>MOYEN</strong> : « regard »",
          "<strong>FAIBLE</strong> : juste un signe de la main pour le départ",
        ],
      },
      autonomie: {
        content: "5 demi-tours consécutifs réussis :",
        items: [
          "0 pose de pied + largeur ≤ 4 m sur 5 passages = validé",
          "Si < 5/5 → analyser : c'est le regard, l'embrayage ou la peur de pencher ?",
        ],
      },
      bilan: {
        content: "Bilan élève d'abord :",
        items: [
          "« Qu'est-ce qui était le plus dur ? »",
          "« Tu sens qu'on peut serrer encore plus, ou pas encore ? »",
          "« Sur 10, à combien tu te sens à l'aise pour passer l'ETM sur cet exercice ? »",
          "Moniteur : 2 réussites + 1 axe + suivante = C2-f (slalom rapide).",
        ],
        notes: "Phrase-clé livret : « Le regard est ton volant. Là où tu regardes, la moto va. »",
      },
    },
  },
  {
    slug: 'freinage-urgence',
    competence: 'C1-g',
    competenceCat: 'C1',
    competenceLabel: 'Maîtriser le maniement de la moto',
    title: "Maîtriser la réalisation d'un freinage",
    kicker: 'C1-g · Plateau · 1h · Individuel',
    accent: '#cf2031',
    icon: 'Octagon',
    duree: '1h',
    summary:
      "Freinage progressif avant + arrière, répartition 70/30, sans blocage. Objectif officiel : 50 km/h, arrêt < 20 m.",
    meta: {
      duree: '1 heure',
      niveau: 'C1-g · Maniement de la moto (REMC)',
      lieu: 'Plateau · piste 50 m balisée',
      materiel: 'Repères au sol · décamètre · gilet jaune · casque-radio',
    },
    // Réutilise les données de l'exemple démo, conservées intactes pour cohérence
    steps: {
      evalStatique: {
        content: "Avant de monter en moto :",
        items: [
          "« Comment s'est passée la dernière leçon ? »",
          "« Quelles difficultés persistent sur le freinage ? »",
          "Vérifier C1-b (démarrer et s'arrêter) validé dans le livret.",
        ],
      },
      evalDynamique: {
        content: "2 freinages à 30 puis 50 km/h sans consigne — observer :",
        items: [
          "Utilisation des 2 freins simultanément ?",
          "Répartition perçue (trop arrière / trop avant) ?",
          "Blocage de roue ou intervention ABS ?",
          "Distance d'arrêt brute mesurée au décamètre",
        ],
      },
      objectif: {
        content:
          "<strong>Objectif officiel livret C1-g</strong> : « À l'issue, l'élève réalise un freinage progressif avant + arrière avec une répartition 70/30 et s'arrête en sécurité. »",
        notes: "Cible officielle : à 50 km/h, arrêt < 20 m sans blocage de roue.",
      },
      criteres: {
        content: "Les 4 critères officiels :",
        items: [
          "Utilisation simultanée des 2 freins",
          "Répartition ≈ 70 % avant, 30 % arrière",
          "Pas de blocage de roue",
          "Distance d'arrêt cohérente avec la vitesse (formule V²/150)",
        ],
        notes: "Phrase-clé : « Avant 70, arrière 30 — c'est ma sécurité. »",
      },
      cours: {
        content: "Apport théorique (10 min) :",
        items: [
          "Schéma profil : transfert de masse vers l'avant lors du freinage",
          "Formule V²/150 pour la distance de freinage",
          "Pourquoi 70/30 : le poids bascule sur la roue avant, l'adhérence aussi",
        ],
      },
      demonstration: {
        content: "Démo décomposée en 3 temps :",
        items: [
          "1) Freinage ARRIÈRE seul (sentir le frein qui ralentit, sans risque)",
          "2) Freinage AVANT seul (sentir la puissance + le transfert de masse)",
          "3) Freinage COMBINÉ avant + arrière (la technique 70/30)",
        ],
      },
      guidage: {
        content: "Pratique (35 min) en guidage dégressif :",
        items: [
          "<strong>FORT</strong> : 30 km/h sur 6 m · guidage verbal pas-à-pas",
          "<strong>MOYEN</strong> : 50 km/h sur 18 m · rappel des points-clés",
          "<strong>FAIBLE</strong> : juste « avant 70 / arrière 30 » au bon moment",
        ],
      },
      autonomie: {
        content: "Évaluation finale (10 min) :",
        items: [
          "50 km/h, arrêt < 20 m, sans blocage (consigne officielle livret)",
          "3 passages consécutifs réussis = C1-g validable",
        ],
      },
      bilan: {
        content: "Tu parles d'abord :",
        items: [
          "« Sur 10, à combien tu te sens à l'aise sur le freinage ? »",
          "« Tu as senti le 70/30 ? À quel moment ? »",
          "« Qu'est-ce que tu pratiques d'ici la prochaine fois ? »",
          "Moniteur : 2-3 réussites + 1 axe + suivante = C1-h (regard en virage).",
        ],
        notes: "Inscrire C1-g validé dans le livret REMC. Phrase-clé : « Avant 70, arrière 30. »",
      },
    },
  },
  {
    slug: 'slalom-allure',
    competence: 'C2-f',
    competenceCat: 'C2',
    competenceLabel: 'Appréhender la route et circuler',
    title: 'Slalom — allure et regard',
    kicker: 'C2-f · Plateau · 45 min · Individuel',
    accent: '#ea580c',
    icon: 'Waves',
    duree: '45 min',
    summary:
      "Slalom 5 cônes à allure soutenue (≥ 30 km/h), regard porté 2 cônes à l'avance, pression alternée sur les repose-pieds.",
    meta: {
      duree: '45 minutes',
      niveau: 'C2-f · Appréhender la route (REMC)',
      lieu: 'Plateau · 5 cônes espacés 5 m',
      materiel: '5 cônes · 2 piquets d\'entrée/sortie',
    },
    steps: {
      evalStatique: {
        content: "Avant pratique :",
        items: [
          "« Comment se passent les virages au ralenti ? »",
          "« Tu as déjà fait du slalom en moto ? »",
          "Vérifier C2-d (tourner ralenti) et C1-g (freinage) validés.",
        ],
      },
      evalDynamique: {
        content: "2 passages à vitesse libre — observer :",
        items: [
          "Le regard est-il sur le cône suivant ou 2 cônes plus loin ?",
          "Freines-tu entre les cônes ? Maintiens-tu l'allure ?",
          "Trajectoire fluide ou saccadée ?",
        ],
      },
      objectif: {
        content:
          "« À la fin, tu sauras enchaîner les 5 cônes à <strong>≥ 30 km/h</strong> sans freiner entre eux, regard porté 2 cônes à l'avance. »",
        notes: "Référence livret REMC : C2-f. Compétence évaluée à l'ETM plateau.",
      },
      criteres: {
        content: "Tu réussis si :",
        items: [
          "Vitesse de passage ≥ 30 km/h (chronométrage)",
          "Pas de freinage entre les cônes",
          "Regard sur cône N+2 (visible au tourner-tête)",
          "Trajectoire fluide, pas de rupture",
          "0 cône touché",
        ],
      },
      cours: {
        content: "5 min :",
        items: [
          "Schéma : trajectoire en S — entrée tangente au cône, sortie alignée",
          "Le secret : <strong>regard 2 cônes à l'avance</strong>, jamais sur le cône en cours",
          "Pression alternée sur le repose-pied opposé au cône (pas le guidon)",
        ],
      },
      demonstration: {
        content: "Démo en 2 temps :",
        items: [
          "1) Démo lente commentée — moniteur exagère le tourner-tête",
          "2) Démo à vitesse réelle — élève observe la fluidité",
        ],
      },
      guidage: {
        content: "Pratique progressive :",
        items: [
          "<strong>FORT</strong> : moniteur compte « 1-2-3-4-5 » au rythme des cônes",
          "<strong>MOYEN</strong> : juste « regard plus loin »",
          "<strong>FAIBLE</strong> : silence — l'élève s'auto-régule",
        ],
      },
      autonomie: {
        content: "5 passages consécutifs à ≥ 30 km/h, 0 cône touché :",
        items: [
          "Chronométrer chaque passage",
          "Si 5/5 OK → on serre les cônes (4 m d'espace au lieu de 5)",
        ],
      },
      bilan: {
        content: "Tu parles d'abord :",
        items: [
          "« Tu sens la différence avec/sans regard 2 cônes à l'avance ? »",
          "« Sur 10, ton aisance ? »",
          "« Sur ETM, tu te sens prêt pour le slalom ? »",
          "Moniteur : 2 réussites + 1 axe + suivante = évitement (C2-g).",
        ],
        notes: "Phrase-clé livret : « Mon regard est mon volant. La moto va où je regarde. »",
      },
    },
  },
];

export const PLATEAU_CATEGORIES = [
  {
    id: 'C1',
    label: 'C1 · Maîtriser le maniement',
    color: '#1d4ed8',
    intent: "Connaître la moto, démarrer, s'arrêter, freiner, équilibre.",
  },
  {
    id: 'C2',
    label: 'C2 · Appréhender la route',
    color: '#7c3aed',
    intent: 'Placement, allure, virages, intersections, slalom.',
  },
];

export function getFichePlateauBySlug(slug) {
  return FICHES_PLATEAU.find((f) => f.slug === slug);
}
